import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import type { ButtonSize, ButtonVariant } from "./Button";
import { cx } from "./cx";
import { Spinner } from "./Spinner";
import "./Button.css";
import "./IconButton.css";

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Required accessible name; also the tooltip (title) unless `title` is given. */
  label: string;
  icon: ReactNode;
  size?: ButtonSize;
  variant?: ButtonVariant;
  loading?: boolean;
}

/** Square, icon-only button. `label` becomes aria-label. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    icon,
    size = "md",
    variant = "ghost",
    loading = false,
    disabled,
    type = "button",
    className,
    title,
    onClick,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={title ?? label}
      className={cx(
        "ui-button",
        "ui-button--icon",
        "ui-icon-button",
        `ui-button--${variant}`,
        `ui-button--${size}`,
        loading && "ui-button--loading",
        className,
      )}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={loading ? (e) => e.preventDefault() : onClick}
      {...rest}
    >
      <span className="ui-button__content">
        <span className="ui-button__icon">{icon}</span>
      </span>
      {loading ? (
        <span className="ui-button__spinner">
          <Spinner size="sm" decorative inheritColor />
        </span>
      ) : null}
    </button>
  );
});
